import { extend } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { IHandlers } from "@dhx/ts-common/types";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { KeyManager, IKeyManager } from "@dhx/ts-common/KeyManager";
import { View } from "@dhx/ts-common/view";
import { Popup } from "@dhx/ts-popup";
import { ISlider, SliderEvents, ISliderConfig, ActiveSlider, ISliderEventHandlersMap } from "./types";
import { getLabelStyle } from "@dhx/ts-common/html";

function normalizeValue(value, min, max) {
	if (value < min) {
		return min;
	}
	if (value > max) {
		return max;
	}
	return value;
}

function parseValue(value: number[] | number | string, min: number, max: number): number[] {
	let values;
	if (value === undefined) {
		values = [];
	} else if (Array.isArray(value)) {
		values = value;
	} else if (typeof value === "string") {
		values = value.split(",").map(v => parseInt(v, 10));
	} else {
		values = [value];
	}
	values[0] = values[0] === undefined ? min : normalizeValue(values[0], min, max);
	values[1] = values[1] === undefined ? max : normalizeValue(values[1], min, max);
	return values;
}

export class Slider extends View implements ISlider {
	public config: ISliderConfig;
	public events: IEventSystem<SliderEvents, ISliderEventHandlersMap>;

	protected _helper: Popup;

	private _offsets: { left: number; top: number };
	private _currentPosition: number;
	private _extraCurrentPosition: number;
	private _length: number;
	private _axis: string;
	private _isExtraActive: boolean;
	private _disabled = false;
	private _isMouseMoving: boolean;
	private _keyManager: IKeyManager;
	private _handlers: IHandlers;
	private _possibleRange: [number, number];
	private _findNewDirection: number;
	private _mouseIn: boolean;
	private _focusIn: boolean;
	private _tooltip: HTMLElement;
	private _activeTooltip: ActiveSlider;

	constructor(container: HTMLElement | string, config: ISliderConfig) {
		super(
			container,
			extend(
				{
					mode: "horizontal",
					min: 0,
					max: 100,
					step: 1,
					tooltip: true,
				},
				config
			)
		);
		this.config.helpMessage = this.config.helpMessage || this.config.help; // TODO: remove suite_7.0
		if (this.config.thumbLabel !== undefined) {
			this.config.tooltip = this.config.thumbLabel; // TODO: remove suite_7.0
		}
		if (this.config.labelInline) {
			this.config.labelPosition = "left"; // TODO: remove suite_7.0
		}
		this.events = new EventSystem(this);
		this._axis = this.config.mode === "horizontal" ? "clientX" : "clientY";
		this._initStartPosition();
		this._keyManager = new KeyManager(() => {
			const activeEl = document.activeElement;
			const element = this.getRootView().refs?.[this._isExtraActive ? "extraRunner" : "runner"]?.el;
			return activeEl === element;
		});
		this._initHotkeys();
		const vNode = create({
			render: () => this._draw(),
			hooks: {
				didMount: () => this._calcSliderPosition(),
				didRedraw: () => this._calcSliderPosition(),
			},
		});
		this._initHandlers();
		this.mount(container, vNode);
	}
	disable(): void {
		this._disabled = true;
		this.paint();
	}
	enable(): void {
		this._disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this._disabled;
	}
	focus(extra?: boolean): void {
		this.getRootView().refs[extra ? "extraRunner" : "runner"].el.focus();
	}
	blur(): void {
		this.getRootView().refs[this._isExtraActive ? "extraRunner" : "runner"].el.blur();
	}
	getValue(): number[] {
		let res;
		if (this.config.range) {
			const a = this._getValue(this._currentPosition);
			const b = this._getValue(this._extraCurrentPosition);
			res = a < b ? [a, b] : [b, a];
		} else {
			res = [this._getValue(this._currentPosition)];
		}
		return res;
	}
	setValue(value: string | number | number[]) {
		const old = this._getValue(this._currentPosition);
		if (Array.isArray(value) && value.length > 1) {
			const oldExtra = this._getValue(this._extraCurrentPosition);
			this._setValue(value[0], false);
			this.events.fire(SliderEvents.change, [value[0], old, false]);
			this._setValue(value[1], true);
			this.events.fire(SliderEvents.change, [value[1], oldExtra, true]);
		} else {
			value = parseFloat(value as string);
			if (!isNaN(value)) {
				this._setValue(value);
				this.events.fire(SliderEvents.change, [value, old, false]);
			} else {
				throw new Error(
					"Wrong value type, for more info check documentation https://docs.dhtmlx.com/suite/slider__api__slider_setvalue_method.html"
				);
			}
		}
		this.paint();
	}
	destructor() {
		this._keyManager && this._keyManager.destructor();
		document.body.contains(this._tooltip) && document.body.removeChild(this._tooltip);
		this._tooltip = null;
		this.unmount();
	}
	private _calcSliderPosition() {
		const root = this.getRootView();
		if (!root) {
			return;
		}
		const tracker: HTMLElement = root.refs.track.el;
		const rect = tracker.getBoundingClientRect();
		this._offsets = {
			left: rect.left + window.pageXOffset,
			top: rect.top + window.pageYOffset,
		};
		this._length = this.config.mode === "horizontal" ? rect.width : rect.height;
	}
	private _initHotkeys() {
		const handlers = {
			arrowLeft: e => {
				e.preventDefault();
				this._move(-this.config.step, e.target.classList.contains("dhx_slider__thumb--extra"));
			},
			arrowRight: e => {
				e.preventDefault();
				this._move(this.config.step, e.target.classList.contains("dhx_slider__thumb--extra"));
			},
			arrowUp: e => {
				e.preventDefault();
				this._move(this.config.step, e.target.classList.contains("dhx_slider__thumb--extra"));
			},
			arrowDown: e => {
				e.preventDefault();
				this._move(-this.config.step, e.target.classList.contains("dhx_slider__thumb--extra"));
			},
		};

		for (const key in handlers) {
			this._keyManager.addHotKey(key, handlers[key]);
		}
	}
	private _move(value: number, forExtra?: boolean) {
		if (this.config.inverse) {
			value = -value;
		}
		const { max, min } = this.config;
		const oldValue = forExtra
			? this._getValue(this._extraCurrentPosition)
			: this._getValue(this._currentPosition);
		let newValue = oldValue + value;

		this._setValue(oldValue + value, forExtra);
		if (newValue > max || newValue < min) {
			newValue = oldValue;
		}
		this.events.fire(SliderEvents.change, [newValue, oldValue, forExtra]);
		this.paint();
	}
	private _initStartPosition() {
		const { max, min, range } = this.config;
		const [value, extraValue] = parseValue(this.config.value, this.config.min, this.config.max);

		this._currentPosition = ((value - min) / (max - min)) * 100;
		if (range) {
			this._extraCurrentPosition = ((max - extraValue) / (max - min)) * 100;
		}
		this._currentPosition = ((value - min) / (max - min)) * 100;
		if (range) {
			this._extraCurrentPosition = ((extraValue - min) / (max - min)) * 100;
		}
		if (this._isInverse()) {
			this._currentPosition = 100 - this._currentPosition;
			if (range) {
				this._extraCurrentPosition = 100 - this._extraCurrentPosition;
			}
		}
	}
	private _getValue(value: number): number {
		if (this._isInverse()) {
			value = 100 - value;
		}
		const { min, max, step } = this.config;
		if (value === 100) {
			return max;
		}
		if (value === 0) {
			return min;
		}
		const val = (value * (max - min)) / 100;
		const remain = val % step;
		const rounder = remain >= step / 2 ? step : 0;
		const result = Number(min) + Number(val) - remain + rounder;
		return +result.toFixed(5);
	}
	private _setValue(val: number, forExtra = false) {
		const { max, min } = this.config;
		if (val > max || val < min) {
			return false;
		}
		const rawValue = ((val - min) / (max - min)) * 100;
		const newValue = this._isInverse() ? 100 - rawValue : rawValue;
		if (forExtra) {
			this._extraCurrentPosition = newValue;
		} else {
			this._currentPosition = newValue;
		}
	}
	private _initHandlers() {
		const sliderMove = (e: MouseEvent & TouchEvent): void => {
			!e.targetTouches && e.preventDefault();
			const currentPosition = e.targetTouches ? e.targetTouches[0][this._axis] : e[this._axis];
			const x = ((currentPosition - this._getBegining()) / this._length) * 100;
			if (this._findNewDirection) {
				if (Math.abs(this._currentPosition - x) < 1) {
					return;
				}
				if (x > this._currentPosition) {
					this._possibleRange = [this._currentPosition, 100];
				} else {
					this._possibleRange = [0, this._currentPosition];
				}
				this._findNewDirection = null;
			}
			if (this._inSide(x)) {
				this._updatePosition(x, this._isExtraActive);
			}
			this.paint();
		};
		const sliderEnd = (e: MouseEvent & TouchEvent): void => {
			this.events.fire(SliderEvents.mouseup, [e]);
			setTimeout(() => {
				this._isMouseMoving = false;
				this.paint();
			}, 4);
			if (!e.targetTouches) {
				document.removeEventListener("mouseup", sliderEnd as any);
				document.removeEventListener("mousemove", sliderMove as any);
			} else {
				document.removeEventListener("touchend", sliderEnd as any);
				document.removeEventListener("touchmove", sliderMove as any);
			}
		};
		const sliderStart = (e: TouchEvent | MouseEvent): void => {
			if (this._disabled || e.which === 3) {
				return;
			}
			this.events.fire(SliderEvents.mousedown, [e]);

			this._isMouseMoving = true;
			let active;
			if ((e.target as HTMLElement).classList.contains("dhx_slider__thumb--extra")) {
				this._isExtraActive = true;
				active = this._extraCurrentPosition;
			} else {
				this._isExtraActive = false;
				active = this._currentPosition;
			}
			this._findNewDirection = null;
			// define possible range
			if (this.config.range) {
				const [more, less] =
					this._currentPosition > this._extraCurrentPosition
						? [this._currentPosition, this._extraCurrentPosition]
						: [this._extraCurrentPosition, this._currentPosition];
				if (this._currentPosition === this._extraCurrentPosition) {
					this._findNewDirection = active;
					this._possibleRange = [0, 100];
				} else if (active < more) {
					this._possibleRange = [0, more];
				} else {
					this._possibleRange = [less, 100];
				}
			} else {
				this._possibleRange = [0, 100];
			}
		};
		if (this.config.helpMessage) {
			this._helper = new Popup({
				css: "dhx_tooltip dhx_tooltip--forced dhx_tooltip--light",
			});
			this._helper.attachHTML(this.config.helpMessage);
		}
		this._handlers = {
			showHelper: e => {
				e.preventDefault();
				e.stopPropagation();
				this._helper.show(e.target);
			},
			onmousedown: (e: MouseEvent) => {
				sliderStart(e);
				document.addEventListener("mousemove", sliderMove as any);
				document.addEventListener("mouseup", sliderEnd as any);
			},
			ontouchstart: (e: TouchEvent) => {
				this._setTooltip(e);
				this._mouseIn = false;
				sliderStart(e);
				document.addEventListener("touchmove", sliderMove as any);
				document.addEventListener("touchend", sliderEnd as any);
				this.paint();
			},
			ontouchend: (e: Event) => {
				this._setTooltip(e);
				this._mouseIn = false;
				this.paint();
			},
			onlabelClick: () => {
				const refs = this.getRootView().refs;
				refs.runner.el.focus();
			},
			onclick: (e: MouseEvent) => {
				if (this._disabled || this._isMouseMoving || e.which === 3) {
					return;
				}
				const x = ((e[this._axis] - this._getBegining()) / this._length) * 100;
				const refs = this.getRootView().refs;
				if (this.config.range) {
					const dist = Math.abs(this._currentPosition - x);
					const extraDist = Math.abs(this._extraCurrentPosition - x);
					if (dist < extraDist) {
						this._updatePosition(x, false);
						refs.runner.el.focus();
					} else {
						this._updatePosition(x, true);
						refs.extraRunner.el.focus();
					}
				} else {
					this._updatePosition(x, false);
					refs.runner.el.focus();
				}
				this.paint();
			},
			onmouseover: (e: Event) => {
				this._setTooltip(e);
				this._mouseIn = true;
				this.paint();
			},
			onmouseout: (e: Event) => {
				this._setTooltip(e);
				this._mouseIn = false;
				this.paint();
			},
			onfocus: (e: Event) => {
				this._setTooltip(e);
				this._focusIn = true;
				this.events.fire(SliderEvents.focus, []);
				this.paint();
			},
			onblur: (e: Event) => {
				this._setTooltip(e);
				this._focusIn = false;
				this.events.fire(SliderEvents.blur, []);
				this.paint();
			},
			onkeydown: (e: KeyboardEvent) => {
				this.events.fire(SliderEvents.keydown, [e]);
			},
		};
	}
	private _getBegining(): number {
		return this.config.mode === "horizontal"
			? this._offsets.left - window.pageXOffset
			: this._offsets.top - window.pageYOffset;
	}
	private _inSide(x: number) {
		const range = this._possibleRange;
		if (x < range[0]) {
			this._updatePosition(range[0], this._isExtraActive);
			return false;
		}
		if (x > range[1]) {
			this._updatePosition(range[1], this._isExtraActive);
			return false;
		}
		return true;
	}
	private _updatePosition(x: number, extra = false) {
		if (x > 100) {
			x = 100;
		}
		if (x < 0) {
			x = 0;
		}
		const { max, min } = this.config;
		const position = extra ? this._extraCurrentPosition : this._currentPosition;
		const oldValue = this._getValue(position);
		const newValue = this._getValue(x);
		if (oldValue === newValue) {
			return;
		}
		const rawValue = ((newValue - min) / (max - min)) * 100;
		const value = this._isInverse() ? 100 - rawValue : rawValue;
		if (extra) {
			this._extraCurrentPosition = value;
		} else {
			this._currentPosition = value;
		}
		this.events.fire(SliderEvents.change, [newValue, oldValue, extra]);
	}
	private _getRunnerStyle(forExtra = false) {
		const direction = this.config.mode === "horizontal" ? "left" : "top";
		const pos = forExtra ? this._extraCurrentPosition : this._currentPosition;
		return {
			[direction]: pos + "%",
		};
	}
	private _isInverse() {
		return (
			(this.config.inverse && this.config.mode === "horizontal") ||
			(!this.config.inverse && this.config.mode === "vertical")
		);
	}
	private _getRunnerCss(forExtra = false) {
		return (
			"dhx_slider__thumb" +
			(forExtra ? " dhx_slider__thumb--extra" : "") +
			(this._isMouseMoving && ((forExtra && this._isExtraActive) || (!forExtra && !this._isExtraActive))
				? " dhx_slider__thumb--active"
				: "") +
			(this._disabled ? " dhx_slider__thumb--disabled" : "") +
			(this._isNullable(forExtra ? this._extraCurrentPosition : this._currentPosition) &&
			!this.config.range
				? " dhx_slider__thumb--nullable"
				: "")
		);
	}
	private _draw() {
		const { labelPosition, mode, hiddenLabel, tick, majorTick, css, helpMessage } = this.config;

		const labelStyle = getLabelStyle({ ...this.config, required: false });
		if (this._tooltip && (!this._mouseIn || !this._focusIn || !this._isMouseMoving)) {
			document.body.contains(this._tooltip) && document.body.removeChild(this._tooltip);
		}
		return el(
			"div",
			{
				class:
					"dhx_slider" +
					" dhx_slider--mode_" +
					mode +
					(labelPosition === "left" ? " dhx_slider--label-inline" : "") +
					(hiddenLabel ? " dhx_slider--label_sr" : "") +
					(tick ? " dhx_slider--ticks" : "") +
					(majorTick ? " dhx_slider--major-ticks" : "") +
					(css ? " " + css : "") +
					(this._disabled ? " dhx_slider--disabled" : ""),
			},
			[
				labelStyle
					? el(
							"label.dhx_label.dhx_slider__label",
							{
								style: labelStyle.style,
								class: helpMessage ? "dhx_label--with-help" : "",
								onclick: this._handlers.onlabelClick,
							},
							helpMessage
								? [
										labelStyle.label && el("span.dhx_label__holder", labelStyle.label),
										el("span.dhx_label-help.dxi.dxi-help-circle-outline", {
											tabindex: "0",
											role: "button",
											onclick: this._handlers.showHelper,
										}),
								  ]
								: labelStyle.label
					  )
					: null,
				this._drawSlider(),
			]
		);
	}
	private _drawSlider() {
		return el(
			".dhx_widget.dhx_slider__track-holder",
			{
				"data-dhx-widget-id": this._uid,
			},
			[
				el(
					".dhx_slider__track",
					{
						_ref: "track",
						onmouseover: this._handlers.onmouseover,
						onmouseout: this._handlers.onmouseout,
						onclick: this._handlers.onclick,
					},
					[
						this._getDetector(),
						el("div", {
							_ref: "runner",
							class: this._getRunnerCss(),
							ontouchstart: this._handlers.ontouchstart,
							ontouchend: this._handlers.ontouchend,
							onmousedown: this._handlers.onmousedown,
							onfocus: this._handlers.onfocus,
							onblur: this._handlers.onblur,
							onkeydown: this._handlers.onkeydown,
							style: this._getRunnerStyle(),
							tabindex: 0,
						}),
						this.config.tooltip && (this._mouseIn || this._focusIn || this._isMouseMoving)
							? this._drawTooltip()
							: null,
						this.config.tooltip &&
						this.config.range &&
						(this._mouseIn || this._focusIn || this._isMouseMoving)
							? this._drawTooltip(true)
							: null,
						this.config.range
							? el("div", {
									_ref: "extraRunner",
									class: this._getRunnerCss(true),
									ontouchstart: this._handlers.ontouchstart,
									ontouchend: this._handlers.ontouchend,
									onmousedown: this._handlers.onmousedown,
									onfocus: this._handlers.onfocus,
									onblur: this._handlers.onblur,
									onkeydown: this._handlers.onkeydown,
									style: this._getRunnerStyle(true),
									tabindex: 0,
							  })
							: null,
					]
				),
				this.config.tick ? this._drawTicks() : null,
			]
		);
	}
	private _getDetector() {
		if (this._disabled) {
			return el(".dhx_slider__range");
		}

		const direction = this.config.mode === "horizontal" ? "left" : "top";
		const size = this.config.mode === "horizontal" ? "width" : "height";
		if (this.config.range) {
			const [more, less] =
				this._currentPosition > this._extraCurrentPosition
					? [this._currentPosition, this._extraCurrentPosition]
					: [this._extraCurrentPosition, this._currentPosition];
			return el(".dhx_slider__range", {
				style: {
					[direction]: less + "%",
					[size]: more - less + "%",
				},
			});
		}
		if (this._isInverse()) {
			return el(".dhx_slider__range", {
				style: {
					[direction]: this._currentPosition + "%",
					[size]: 100 - this._currentPosition + "%",
				},
			});
		}
		return el(".dhx_slider__range", {
			style: {
				[direction]: 0,
				[size]: this._currentPosition + "%",
			},
		});
	}
	private _drawTooltip(forExtra = false) {
		if (this._activeTooltip === "none" || !this.getRootView()) return;

		const pos =
			this._activeTooltip === "extraTooltip" ? this._extraCurrentPosition : this._currentPosition;
		const direction = this.config.mode === "horizontal" ? "left" : "top";
		let classNameModifiers = "";

		if ((forExtra && this._isExtraActive) || (!forExtra && !this._isExtraActive)) {
			classNameModifiers += " dhx_slider__thumb-label--active";
		}
		if (!this._tooltip) {
			this._tooltip = document.createElement("div");
		}

		const coords =
			this._activeTooltip === "tooltip"
				? this.getRootView().refs.runner.el.getBoundingClientRect()
				: this.getRootView().refs.extraRunner.el.getBoundingClientRect();

		this._tooltip.className = "dhx_slider__thumb-label" + classNameModifiers;
		this._tooltip.style.left = coords.x + (direction === "left" ? 6 : -30) + window.pageXOffset + "px";
		this._tooltip.style.top = coords.y + (direction === "left" ? -30 : 6) + window.pageYOffset + "px";

		this._tooltip.innerText = this._getValue(pos).toString();

		document.body.appendChild(this._tooltip);
	}
	private _getTicks() {
		const { max, min, step, tick, majorTick } = this.config;
		const len = max - min;
		const tickLength = (step * tick) / len;
		const positions = [];
		let length = 0;
		let index = 0;

		while (length < 1) {
			const tickValue = +(Number(min) + length * len).toFixed(5);
			const isMultiple = index % majorTick === 0;
			positions.push({
				position: (this._isInverse() ? (1 - length) * 100 : length * 100) + "%",
				isMultiple,
				label:
					isMultiple && typeof this.config.tickTemplate === "function"
						? this.config.tickTemplate(tickValue)
						: null,
			});
			length += tickLength;
			index++;
		}
		positions.push({
			position: (this._isInverse() ? 0 : 100) + "%",
			isMultiple: true,
			label: typeof this.config.tickTemplate === "function" ? this.config.tickTemplate(max) : null,
		});
		return positions;
	}
	private _drawTicks() {
		const direction = this.config.mode === "horizontal" ? "left" : "top";
		return el(
			".dhx_slider__ticks-holder",
			this._getTicks().map(tick =>
				el(
					"div",
					{
						class: "dhx_slider__tick" + (tick.isMultiple ? " dhx_slider__tick--major" : ""),
						style: {
							[direction]: tick.position,
						},
					},
					tick.label !== undefined ? [el(".dhx_slider__tick-label", tick.label)] : null
				)
			)
		);
	}
	private _isNullable(value) {
		if (this._isInverse()) {
			return value === 100;
		} else {
			return value === 0;
		}
	}
	private _setTooltip(e): void {
		if (e.target.classList.contains("dhx_slider__thumb--extra")) {
			this._activeTooltip = "extraTooltip";
		} else if (e.target.classList.contains("dhx_slider__thumb")) {
			this._activeTooltip = "tooltip";
		} else {
			this._activeTooltip = "none";
		}
	}
}
